// Portfolio Administration v1.0 PR 4a — right-side detail drawer shared
// by the Insurance / UCC / Vendors / Due Dates tabs. Renders a focused
// resource detail surface with scroll body + sticky footer. Modeled on
// the existing <Modal> primitive but anchored to the right edge.

import { useId } from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useFocusTrap, useOverlayDismiss } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

export interface ResourceDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Optional controls rendered in the header, to the left of the close
   *  button (e.g. a "open in full queue" escape hatch). */
  headerActions?: ReactNode;
  width?: "md" | "lg" | "xl" | "2xl";
  testId?: string;
  dismissOnBackdropClick?: boolean;
}

const WIDTH_CLASS: Record<NonNullable<ResourceDrawerProps["width"]>, string> = {
  md: "max-w-xl",
  lg: "max-w-2xl",
  // External Portal — wide enough for an inline file viewer + the review
  // panel's two-column body in the staging slide-over.
  xl: "max-w-5xl",
  // Buyer-submission record drawer — its Overview's five-vital strip + rail
  // need the room; `xl` is pinned by AllocationDrawer and stays as it is.
  "2xl": "max-w-6xl",
};

export function ResourceDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  headerActions,
  width = "md",
  testId,
  dismissOnBackdropClick = true,
}: ResourceDrawerProps) {
  // Per-instance, so two drawers open at once (one deep-linked behind another,
  // or a drawer that opens a second drawer) do not both claim `drawer-title`.
  // A duplicate id makes `aria-labelledby` resolve to whichever node the
  // document happens to hold first, so a screen reader announces the wrong
  // drawer's heading — silently, since the markup is valid either way.
  const titleId = useId();

  // Focus management, scoped to the drawer CARD so the header's Close button is
  // inside the Tab cycle — matching `Modal`, which was wired to the same hook
  // on 2026-08-02. Without it, opening a drawer left focus on the trigger
  // behind the backdrop: Tab walked the page underneath, and a keyboard or
  // screen-reader user had no way into the surface that had just appeared.
  const trap = useFocusTrap<HTMLDivElement>({ active: open });

  // Escape and the scroll lock, stack-aware. This drawer can open a second
  // drawer (the attention feed opens a due-date drawer inside itself) and
  // thirteen surfaces pair one with a Modal; per-instance listeners meant one
  // Escape closed the lot.
  useOverlayDismiss(open, onClose);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-onyx/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={dismissOnBackdropClick ? onClose : undefined}
      data-testid={testId}
    >
      <div
        ref={trap.containerRef}
        onKeyDown={trap.onKeyDown}
        className={cn(
          "h-full w-full bg-white-pure shadow-lg border-l border-border-light",
          "flex flex-col animate-slide-in-right",
          WIDTH_CLASS[width],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-space-3 p-space-4 border-b border-border-light">
          <div className="flex-1 min-w-0">
            <h2
              id={titleId}
              className="font-display font-bold text-h3 text-onyx tracking-normal break-words m-0"
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-caption text-gray-mid mt-space-1 m-0">
                {subtitle}
              </p>
            )}
          </div>
          {/* NOT `shrink-0`. The title column is `flex-1 min-w-0` (basis 0), so a
              cluster that cannot shrink keeps its full natural width and, once
              that width alone fills the row, carries the close button past the
              panel's edge — x 605–631 in a 600px viewport with the buyer-
              submission drawer's actions. Dropping `shrink-0` lets the cluster
              shrink to the row, `min-w-0` lets it go below its min-content on a
              still narrower row, `flex-wrap` gives the close button a second
              line inside it, and `justify-end` keeps that line on the right —
              the `PageHeader` idiom. It does not give the title back its width:
              with a basis of 0 the title gets only what the cluster leaves, so
              a cluster as wide as the row less the gap still leaves it 0px wide
              (a separate decision). */}
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-space-3">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-space-1 rounded-full hover:bg-white-warm"
              aria-label="Close"
            >
              <X size={18} aria-hidden />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-space-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-space-2 p-space-4 border-t border-border-light bg-white-warm">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
